import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { RefreshControl, View } from 'react-native';
import { useIsFocused, useNavigation } from '@react-navigation/native';

import { useUserContext } from '../../../common/utils/context';
import { MY_DAY_STEP_STATUS } from '../../../common/utils/myDay/driverDaySteps';
import { getLocalTime } from '../../../common/utils/time/time';
import { formatDate } from '../../../common/utils/utils';
import { ActionDock, EmptyState, ProgressBar, Screen, StepRow } from '../../design/components';
import { STEP_STATUS } from '../../design/components/StatusIcon';
import { useRefreshPage } from '../../hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import DriverClockBadge from './DriverClockBadge';
import { useDriverDay, useDriverShell } from './DriverDayProvider';
import DriverTrainingBanner from './DriverTrainingBanner';
import { DRIVER_ROUTE, RUN_LOAD_ERROR } from './driverRoutes';
import BriefSheet from './startOfDay/BriefSheet';
import ChooseVehicleSheet from './startOfDay/ChooseVehicleSheet';
import { getStartOfDayAfterThis, getStartStepSyncState } from './startOfDay/startOfDaySteps';
import useBriefRead from './startOfDay/useBriefRead';
import {
	getOptionalStepTags,
	getPhaseStepStatus,
	getRequiredStepProgress,
	getSignOffStepStatus,
} from './stepStatus';
import { STEP_SYNC_STATE } from './stepSyncState';
import { getStopPositionName, getStopSiteName } from './stopDetails';
import useDriverDayActions from './useDriverDayActions';

const SHEET = {
	BRIEF: 'brief',
	VEHICLE: 'vehicle',
};

// Dock button for the step the driver should do next.
const STEP_ACTION_LABEL = {
	clockIn: 'Open shift pass',
	brief: 'Read run brief',
	runOverview: 'Review run overview',
	vehicle: 'Confirm vehicle',
	accessKeys: 'Review keys and access',
	packAndPick: 'Recalculate and top up pick',
	ticketsSummary: 'Read tickets summary',
	leaveWarehouseChecklist: 'Open leave warehouse checklist',
};

const countLabel = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

const getStepSubtitle = (step) => {
	if (step.status !== MY_DAY_STEP_STATUS.BLOCKED && step.badgeCount) {
		if (step.key === 'accessKeys') {
			return `Keys, passes and PPE for ${countLabel(step.badgeCount, 'stop', 'stops')} today`;
		}
		if (step.key === 'ticketsSummary') {
			return `${countLabel(step.badgeCount, 'ticket', 'tickets')} to read and pack for`;
		}
	}

	return step.description;
};

const getDoneStatus = (step, syncState) => {
	if (syncState === STEP_SYNC_STATE.PENDING) {
		return { statusText: 'Saved on phone', statusTone: 'warning' };
	}
	if (syncState === STEP_SYNC_STATE.FAILED) {
		return { statusText: 'Sync failed', statusTone: 'warning' };
	}
	if (step.key === 'brief') {
		return { statusText: 'Reopen', statusTone: 'accent' };
	}
	if (step.key === 'runOverview' && step.badgeCount) {
		return { statusText: countLabel(step.badgeCount, 'stop', 'stops') };
	}

	return getSignOffStepStatus(step) || { statusText: 'Done' };
};

const StartStepRow = memo(({ step, status, syncState, onPress }) => {
	const isDone = status === STEP_STATUS.DONE;
	const doneStatus = isDone ? getDoneStatus(step, syncState) : {};

	return (
		<StepRow
			status={status}
			title={step.label}
			subtitle={getStepSubtitle(step)}
			statusText={doneStatus.statusText}
			statusTone={doneStatus.statusTone}
			tags={getOptionalStepTags(step)}
			onPress={step.trainingUnavailable ? undefined : () => onPress(step)}
		/>
	);
});

/**
 * Start of day: the warehouse steps from driverDaySteps (status circles on
 * the left), each opening its page or sheet, and one pinned button that
 * follows the next step. Starting the run moves on to On the run.
 */
const StartOfDayView = () => {
	const styles = useThemedStyles(createStyles);
	const navigation = useNavigation();
	const isFocused = useIsFocused();
	const { tenant, tenantSettings } = useUserContext();
	const { isMyDayEnabled } = useDriverShell();
	const day = useDriverDay();
	const {
		serviceRun,
		upcomingRun,
		isTraining,
		trainingOffline,
		isOnline,
		scopedQueue,
		todayDateKey,
		sortedDockets,
		nextIncompleteDocket,
		visibleStartSteps,
		nextAction,
		activePhaseKey,
		runStarted,
	} = day;
	const [refreshPage, isRefreshing] = useRefreshPage(day.refresh);
	const [openSheet, setOpenSheet] = useState(null);
	const closeSheet = useCallback(() => setOpenSheet(null), []);
	const { markRead: markBriefRead } = useBriefRead(serviceRun, day.isTraining);
	const closeBriefSheet = useCallback(() => {
		markBriefRead();
		closeSheet();
	}, [closeSheet, markBriefRead]);
	const actions = useDriverDayActions(day, { onStepConfirmed: closeSheet });
	const {
		openClockPass,
		openStepChecklist,
		requireTrainingConnection,
		startServiceRun,
		isConfirmingStep,
		isConfirmingOnlineStep,
		acknowledgeBrief,
		isAcknowledgingBrief,
		selectVehicle,
		confirmVehicle,
		isUpdatingVehicle,
	} = actions;

	// Once the run starts from here, carry on to On the run.
	const startRequestedRef = useRef(false);
	useEffect(() => {
		if (runStarted && startRequestedRef.current && isFocused) {
			startRequestedRef.current = false;
			navigation.replace(DRIVER_ROUTE.RUN_LIST);
		}
	}, [isFocused, navigation, runStarted]);
	const startRun = useCallback(() => {
		startRequestedRef.current = true;
		startServiceRun();
	}, [startServiceRun]);

	const openStep = useCallback((step) => {
		switch (step.key) {
			case 'clockIn':
				openClockPass();
				break;
			case 'brief':
				setOpenSheet(SHEET.BRIEF);
				break;
			case 'vehicle':
				if (requireTrainingConnection()) {
					setOpenSheet(SHEET.VEHICLE);
				}
				break;
			case 'runOverview':
				navigation.push(DRIVER_ROUTE.RUN_OVERVIEW);
				break;
			case 'accessKeys':
				navigation.push(DRIVER_ROUTE.KEYS_ACCESS);
				break;
			case 'packAndPick':
				navigation.push(DRIVER_ROUTE.RECALC_PICK);
				break;
			case 'ticketsSummary':
				navigation.push('TicketSummaryView', { id: serviceRun?.id, startOfDayStep: true });
				break;
			default: {
				// Same "Step n of m" as the other start-of-day step pages (useStartOfDayStep).
				const stepIndex = visibleStartSteps.findIndex(item => item.key === step.key);
				openStepChecklist(step.key, stepIndex >= 0 ? { number: stepIndex + 1, total: visibleStartSteps.length } : undefined);
			}
		}
	}, [navigation, openClockPass, openStepChecklist, requireTrainingConnection, serviceRun?.id, visibleStartSteps]);

	const submitBrief = useCallback(async () => {
		if (await acknowledgeBrief()) {
			closeSheet();
		}
	}, [acknowledgeBrief, closeSheet]);

	const renderDock = () => {
		if (!nextAction) {
			return null;
		}

		if (nextAction.type === 'start') {
			const { step } = nextAction;
			return (
				<ActionDock
					hint={getStartOfDayAfterThis(visibleStartSteps, step.key, runStarted)}
					action={{
						label: STEP_ACTION_LABEL[step.key] || step.label,
						onPress: () => openStep(step),
						disabled: step.key === 'vehicle' && trainingOffline,
					}}
				/>
			);
		}

		if (nextAction.type === 'startRun') {
			// Runs whose top-up was marked not needed recheck the pick online first.
			const needsConnection = Boolean(serviceRun?.myDayPickTopUpStatus?.markedNotNeeded);
			// Place first (decision 6): site · position.
			const firstStopName = nextIncompleteDocket
				? [getStopSiteName(nextIncompleteDocket), getStopPositionName(nextIncompleteDocket)].filter(Boolean).join(' · ')
				: null;
			// Optional steps (e.g. Clock in, Leave warehouse) may still be open here.
			const readyText = visibleStartSteps.some(getOptionalStepTags) ? 'Required steps are done.' : 'Everything is ready.';
			return (
				<ActionDock
					hint={firstStopName ? `${readyText} First stop: ${firstStopName}` : readyText}
					action={{
						label: needsConnection && !isOnline ? 'Connect to start service run' : 'Start service run',
						onPress: startRun,
						loading: needsConnection ? isConfirmingOnlineStep : isConfirmingStep,
						disabled: trainingOffline || (needsConnection && !isOnline),
					}}
				/>
			);
		}

		const isEnd = activePhaseKey === 'end';
		return (
			<ActionDock
				hint="Start of day is done."
				action={{
					label: isEnd ? 'Continue end of day' : 'Continue on the run',
					onPress: () => navigation.navigate(isEnd ? DRIVER_ROUTE.END_OF_DAY : DRIVER_ROUTE.RUN_LIST),
				}}
			/>
		);
	};

	const renderEmpty = () => {
		if (!isMyDayEnabled && !isTraining) {
			return (
				<EmptyState
					title="No start of day steps"
					message="Your runs go straight to the run list."
					actionLabel="Open run"
					onAction={() => navigation.navigate(DRIVER_ROUTE.RUN_LIST)}
				/>
			);
		}
		if (isTraining) {
			return (
				<EmptyState
					title="Run unavailable"
					message="This run is no longer available for training. Go back to the employee preview and choose another run."
				/>
			);
		}

		return (
			<EmptyState
				title="No run today"
				message={upcomingRun
					? `Next run: ${formatDate(upcomingRun.planned, 'DAY_MONTHWORD_YEAR')}${upcomingRun.route?.name ? ` · ${upcomingRun.route.name}` : ''}`
					: 'See your manager if you are expecting a run.'}
			/>
		);
	};

	const hasRun = Boolean(serviceRun) && (isMyDayEnabled || isTraining);
	const requiredProgress = getRequiredStepProgress(visibleStartSteps);
	const nextStepKey = nextAction?.type === 'start' ? nextAction.step.key : null;
	const subheading = hasRun
		? [
			serviceRun.route?.name || 'Routeless service run',
			getLocalTime(tenant?.timezone).format('ddd D MMM'),
			countLabel(sortedDockets.length, 'machine', 'machines'),
		].join(' · ')
		: undefined;

	return (
		<Screen
			backLabel="Home"
			headerRight={<DriverClockBadge />}
			heading="Start of day"
			subheading={subheading}
			isLoading={day.isLoading}
			error={day.loadError ? RUN_LOAD_ERROR : undefined}
			onRetry={day.refresh}
			banner={<DriverTrainingBanner />}
			dock={hasRun ? renderDock() : null}
			refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={refreshPage} />}
		>
			{hasRun ? (
				<>
					{requiredProgress.totalCount > 0 && (
						<ProgressBar
							done={requiredProgress.doneCount}
							total={requiredProgress.totalCount}
							label={requiredProgress.label}
							hint={runStarted ? 'Out on the run' : 'Then out on the run'}
						/>
					)}
					<View style={styles.steps}>
						{visibleStartSteps.map(step => (
							<StartStepRow
								key={step.key}
								step={step}
								status={getPhaseStepStatus(step, nextStepKey)}
								syncState={step.status === MY_DAY_STEP_STATUS.DONE
									? getStartStepSyncState(step, { queue: scopedQueue, serviceRunId: serviceRun.id, todayDateKey })
									: null}
								onPress={openStep}
							/>
						))}
					</View>
					{!sortedDockets.length && (
						<EmptyState title="No stops on this run yet" message="See your manager if you are expecting some." />
					)}
				</>
			) : renderEmpty()}
			{hasRun && (
				<>
					<BriefSheet
						isVisible={openSheet === SHEET.BRIEF}
						onClose={closeBriefSheet}
						serviceRun={serviceRun}
						requireAcknowledgement={Boolean(tenantSettings?.myDayRequireBriefAcknowledgement)}
						onAcknowledge={submitBrief}
						isAcknowledging={isAcknowledgingBrief}
						disabled={trainingOffline}
					/>
					<ChooseVehicleSheet
						isVisible={openSheet === SHEET.VEHICLE}
						onClose={closeSheet}
						assignedVehicle={serviceRun.vehicle}
						onSelectVehicle={selectVehicle}
						onConfirmVehicle={confirmVehicle}
						loading={isUpdatingVehicle}
						disabled={trainingOffline}
					/>
				</>
			)}
		</Screen>
	);
};

const createStyles = ({ ui: { space } }) => ({
	steps: {
		gap: space.sm,
	},
});

export default StartOfDayView;
